import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
	Accordion, AccordionContent, AccordionItem, AccordionTrigger,
	Badge,
	Button,
	FileUploadCard,
	FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
	Input,
	Switch,
	Textarea,
	TextField,
} from '@rentflowdev/ui'
import { useGenerateBuilderUploadUrlMutation } from '@/api'
import { HEX_COLOR_REGEX, type BuilderProfileFormValues } from './schema'

const BRANDING_COLOR_FIELDS = [
	{ name: 'branding.primaryColor',    label: 'Основной' },
	{ name: 'branding.accentColor',     label: 'Акцент' },
	{ name: 'branding.backgroundColor', label: 'Фон' },
] as const

const FEATURE_FIELDS = [
	{ name: 'features.payments',  label: 'Платежи' },
	{ name: 'features.documents', label: 'Документы' },
	{ name: 'features.archive',   label: 'Архив' },
] as const

const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg']
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024

function formatFileSize(bytes: number): string {
	if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
	return `${Math.max(1, Math.round(bytes / 1024))} КБ`
}

/** Logo picker: presigned S3 upload via POST /builder/upload-url + xhr PUT. */
function LogoField() {
	const form = useFormContext<BuilderProfileFormValues>()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [generateUploadUrl] = useGenerateBuilderUploadUrlMutation()
	const [uploadingFile, setUploadingFile] = useState<{ name: string; size: number } | null>(null)
	const [progress, setProgress] = useState(0)

	function openPicker() {
		fileInputRef.current?.click()
	}

	async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file) return
		form.clearErrors('branding.logo')
		if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
			form.setError('branding.logo', { message: 'Только PNG или JPG' })
			return
		}
		if (file.size > MAX_LOGO_SIZE_BYTES) {
			form.setError('branding.logo', { message: 'Файл больше 2 МБ' })
			return
		}
		setUploadingFile({ name: file.name, size: file.size })
		setProgress(0)
		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()
			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.upload.onprogress = (ev) => {
					if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100))
				}
				xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})
			form.setValue('branding.logo', publicUrl, { shouldDirty: true, shouldValidate: true })
		} catch (err) {
			console.error('logo upload failed:', err)
			form.setError('branding.logo', { message: 'Не удалось загрузить логотип' })
		} finally {
			setUploadingFile(null)
		}
	}

	return (
		<FormField
			control={form.control}
			name="branding.logo"
			render={({ field }) => (
				<FormItem>
					<FormLabel>Логотип</FormLabel>
					{uploadingFile ? (
						<FileUploadCard
							state="loading"
							fileName={uploadingFile.name}
							fileSize={formatFileSize(uploadingFile.size)}
							progress={progress}
							loadingLabel="Загрузка…"
						/>
					) : field.value ? (
						<div className="flex items-center gap-3">
							<img
								src={field.value}
								alt="Логотип приложения"
								className="size-16 shrink-0 rounded-xl border border-(--color-border) object-cover"
							/>
							<div className="flex flex-col items-start gap-1">
								<Button type="button" variant="outline" size="sm" onClick={openPicker}>
									Заменить
								</Button>
								<Button type="button" variant="ghost" size="sm" onClick={() => field.onChange('')}>
									Удалить
								</Button>
							</div>
						</div>
					) : (
						<FileUploadCard
							state="idle"
							fileName="Логотип приложения"
							fileSize="PNG или JPG до 2 МБ"
							actionLabel="Загрузить"
							onAction={openPicker}
						/>
					)}
					<FormDescription>Без логотипа будет использован стандартный</FormDescription>
					<FormMessage />
					<input
						ref={fileInputRef}
						type="file"
						accept={ACCEPTED_LOGO_TYPES.join(',')}
						className="hidden"
						onChange={(e) => void handleFilePicked(e)}
					/>
				</FormItem>
			)}
		/>
	)
}

interface BuilderProfileFormFieldsProps {
	mode: 'create' | 'edit'
	/** Whether the profile already stores RuStore credentials (edit mode). */
	hasRustoreCredentials?: boolean
	/**
	 * Clears the stored RuStore credentials (edit mode). The handler must send
	 * explicit empty strings — `undefined` keeps the stored pair.
	 */
	onClearRustoreCredentials?: () => void
	/** In-flight state of the clear-credentials request. */
	isClearingRustoreCredentials?: boolean
}

/** Consumer-facing build-profile field set, shared by the create dialog and the edit card. */
export function BuilderProfileFormFields({
	mode,
	hasRustoreCredentials = false,
	onClearRustoreCredentials,
	isClearingRustoreCredentials = false,
}: BuilderProfileFormFieldsProps) {
	const form = useFormContext<BuilderProfileFormValues>()

	return (
		<div className="flex flex-col gap-6">
			<TextField name="appName" label="Название приложения" placeholder="ACME Аренда" />

			<LogoField />

			<section className="flex flex-col gap-3">
				<h3 className="text-base font-[500] text-(--color-foreground)">Цвета бренда</h3>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					{BRANDING_COLOR_FIELDS.map(({ name, label }) => (
						<FormField
							key={name}
							control={form.control}
							name={name}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{label}</FormLabel>
									<div className="flex items-center gap-2">
										<input
											type="color"
											aria-label={label}
											value={HEX_COLOR_REGEX.test(field.value ?? '') ? field.value : '#000000'}
											onChange={(e) => field.onChange(e.target.value.toUpperCase())}
											className="size-10 shrink-0 cursor-pointer rounded-md border border-(--color-border) bg-transparent p-1 [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none"
										/>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ''}
												placeholder="#RRGGBB"
												className="flex-1"
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					))}
				</div>
			</section>

			<section className="flex flex-col gap-3">
				<h3 className="text-base font-[500] text-(--color-foreground)">Функции</h3>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					{FEATURE_FIELDS.map(({ name, label }) => (
						<FormField
							key={name}
							control={form.control}
							name={name}
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between gap-3 rounded-md border border-(--color-border) px-3 py-2.5">
									<FormLabel className="font-[500]">{label}</FormLabel>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={(checked) => field.onChange(checked === true)}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					))}
				</div>
			</section>

			<section className="flex flex-col gap-3">
				<h3 className="text-base font-[500] text-(--color-foreground)">Приложение в RuStore</h3>
				<FormField
					control={form.control}
					name="storeMeta.shortDescription"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Краткое описание</FormLabel>
							<FormControl>
								<Input
									{...field}
									value={field.value ?? ''}
									maxLength={80}
									placeholder="Аренда жилья без посредников"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="storeMeta.fullDescription"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Полное описание</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									value={field.value ?? ''}
									maxLength={4000}
									className="min-h-24"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<TextField
					name="storeMeta.email"
					label="Email поддержки"
					type="email"
					placeholder="support@acme.example"
				/>
			</section>

			<Accordion type="single" collapsible>
				<AccordionItem
					value="rustore-keys"
					className="rounded-md border border-(--color-border) px-4"
				>
					<AccordionTrigger className="py-3 text-base font-[500]">
						<span className="flex flex-wrap items-center gap-2">
							Публикация в RuStore (ключи)
							{mode === 'edit' && hasRustoreCredentials && (
								<Badge variant="success">Ключи сохранены</Badge>
							)}
						</span>
					</AccordionTrigger>
					<AccordionContent className="flex flex-col gap-3">
						{mode === 'edit' && hasRustoreCredentials && (
							<p className="text-sm text-(--color-muted-foreground)">
								Оставьте поля пустыми, чтобы сохранить текущие ключи
							</p>
						)}
						<FormField
							control={form.control}
							name="rustoreKeyId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ID ключа RuStore</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={field.value ?? ''}
											placeholder="1234567890"
											autoComplete="off"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="rustorePrivateKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Приватный ключ (PEM или base64)</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											value={field.value ?? ''}
											className="min-h-24 font-mono"
											autoComplete="off"
											spellCheck={false}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{mode === 'edit' && hasRustoreCredentials && onClearRustoreCredentials && (
							<div>
								<Button
									type="button"
									variant="outline"
									disabled={isClearingRustoreCredentials}
									onClick={onClearRustoreCredentials}
								>
									Удалить ключи
								</Button>
							</div>
						)}
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	)
}
